import { NavLink } from 'react-router-dom'
import Tooltip from '@/components/ui/Tooltip'
import {
  LayoutDashboard,
  Users,
  FileText,
  DollarSign,
  Activity,
  Settings,
  Briefcase,
  ChevronLeft,
  ChevronRight,
  Handshake,
  Building2,
  X,
  Kanban,
  ClipboardList,
  Receipt,
  BarChart2,
  Shield,
} from 'lucide-react'
import { useAppStore } from '@/store/useAppStore'
import { useIsAdmin } from '@/hooks/useIsAdmin'
import { cn } from '@/lib/utils'

const navItems = [
  { label: 'Dashboard',      icon: LayoutDashboard, to: '/app' },
  { label: 'Vivier',         icon: Users,           to: '/app/vivier' },
  { label: 'Mandats',        icon: FileText,        to: '/app/mandats' },
  { label: 'Pipeline',       icon: Kanban,          to: '/app/pipeline' },
  { label: 'Commissions',    icon: DollarSign,      to: '/app/commissions' },
  { label: 'Missions',       icon: ClipboardList,   to: '/app/missions' },
  { label: 'Co-recrutement', icon: Handshake,       to: '/app/collaborations' },
  { label: 'Clients',        icon: Building2,       to: '/app/clients' },
  { label: 'Partenaires',    icon: Building2,       to: '/app/partenaires' },
  { label: 'Facturation',    icon: Receipt,         to: '/app/facturation' },
  { label: 'Activité',       icon: Activity,        to: '/app/activite' },
  { label: 'Paramètres',     icon: Settings,        to: '/app/settings' },
]

const adminNavItems = [
  { label: 'Utilisateurs', icon: Shield,    to: '/app/admin/users'     },
  { label: 'Analytics',    icon: BarChart2, to: '/app/admin/analytics' },
]

interface NavItemDef {
  label: string
  icon?: React.ComponentType<{ size?: number; className?: string }>
  to?: string
  type?: string
  color?: string
  children?: NavItemDef[]
}

interface SidebarProps {
  mobileOpen: boolean
  onClose: () => void
}

export default function Sidebar({ mobileOpen, onClose }: SidebarProps) {
  const { sidebarCollapsed, setSidebarCollapsed } = useAppStore()
  const isAdmin = useIsAdmin()

  return (
    <aside
      className={cn(
        // Base
        'h-screen bg-white dark:bg-slate-900 border-r border-gray-100 dark:border-slate-800 flex flex-col transition-all duration-300 shrink-0',
        // Mobile : drawer fixe qui slide depuis la gauche
        'fixed inset-y-0 left-0 z-50 w-72',
        mobileOpen ? 'translate-x-0' : '-translate-x-full',
        // Desktop : statique dans le flux flex, replié ou non
        'md:static md:translate-x-0 md:z-auto',
        sidebarCollapsed ? 'md:w-16' : 'md:w-56',
      )}
    >
      {/* Logo + bouton fermer (mobile) */}
      <div
        className={cn(
          'flex items-center gap-3 px-4 py-5 border-b border-gray-100 dark:border-slate-800',
          sidebarCollapsed && 'md:justify-center md:px-0',
        )}
      >
        <div className="w-8 h-8 rounded-lg bg-[#4F46E5] flex items-center justify-center shrink-0">
          <Briefcase size={16} className="text-white" />
        </div>
        <span className={cn('font-bold text-gray-900 dark:text-slate-100 text-base flex-1', sidebarCollapsed && 'md:hidden')}>
          RecrutPro
        </span>
        {/* Croix visible uniquement sur mobile */}
        <button
          onClick={onClose}
          className="p-1.5 rounded-lg text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:text-gray-400 dark:hover:text-slate-300 hover:bg-gray-100 dark:hover:bg-gray-600 dark:bg-gray-700 dark:hover:bg-slate-800 transition md:hidden"
          aria-label="Fermer le menu"
        >
          <X size={18} />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
        {navItems.map((item) =>
          item.to != null && item.icon != null ? (
            <NavItem
              key={item.to}
              item={item as NavItemDef & { to: string; icon: React.ComponentType<{ size?: number; className?: string }> }}
              collapsed={sidebarCollapsed}
              onNavigate={onClose}
            />
          ) : null,
        )}
        {/* Admin nav items */}
        {isAdmin && (
          <>
            {!sidebarCollapsed && (
              <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 dark:text-slate-600 px-3 pt-4 pb-1">
                Admin
              </p>
            )}
            {adminNavItems.map((item) => (
              <NavItem
                key={item.to}
                item={item as NavItemDef & { to: string; icon: React.ComponentType<{ size?: number; className?: string }> }}
                collapsed={sidebarCollapsed}
                onNavigate={onClose}
              />
            ))}
          </>
        )}
      </nav>

      {/* Bouton replier — desktop uniquement */}
      <div className="border-t border-gray-100 dark:border-slate-800 p-2 hidden md:block">
        <Tooltip content={sidebarCollapsed ? 'Agrandir le menu' : 'Réduire le menu'} position="right">
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="w-full flex items-center justify-center py-2 rounded-lg text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:text-gray-400 dark:hover:text-slate-300 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-900 dark:hover:bg-slate-800 transition"
          >
            {sidebarCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
            {!sidebarCollapsed && <span className="ml-2 text-xs">Réduire</span>}
          </button>
        </Tooltip>
      </div>
    </aside>
  )
}

function NavItem({
  item,
  collapsed,
  onNavigate,
}: {
  item: NavItemDef & { to: string; icon: React.ComponentType<{ size?: number; className?: string }> }
  collapsed: boolean
  onNavigate?: () => void
}) {
  const Icon = item.icon
  return (
    <NavLink
      to={item.to}
      end={item.to === '/app'}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 px-3 py-2.5 md:py-2 rounded-lg text-sm font-medium transition',
          isActive
            ? 'bg-[#EEF2FF] dark:bg-[#4F46E5]/20 text-[#4F46E5]'
            : 'text-gray-600 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-900 dark:hover:bg-slate-800 hover:text-gray-900 dark:text-white dark:hover:text-slate-100',
          // Sur desktop replié : centrer l'icône
          collapsed && 'md:justify-center md:px-0',
        )
      }
      title={collapsed ? item.label : undefined}
    >
      <Icon size={18} className="shrink-0" />
      {/* Label : toujours visible sur mobile, caché si replié sur desktop */}
      <span className={cn(collapsed && 'md:hidden')}>{item.label}</span>
    </NavLink>
  )
}
